import { apiFetch } from "./apiFetch";

export type PartnerStatus = "Active" | "Inactive";

export type PartnerLinkPlatform =
  | "Website"
  | "Instagram"
  | "Facebook"
  | "Twitter"
  | "LinkedIn"
  | "YouTube"
  | "TikTok"
  | "Discourse"
  | "Other";

export interface PartnerLink {
  platform: PartnerLinkPlatform;
  url: string;
  label?: string;
  iconUrl?: string;
}

/**
 * The greeting shown to a member the first time they arrive in a video session.
 * Ops-authored here; there is no client write path.
 */
export interface PartnerWelcomeNote {
  /** Max 80 characters. */
  title: string;
  /**
   * Max 1000 characters. Plain text: literal newlines are line breaks and
   * `**…**` marks bold. No other markup is interpreted.
   */
  body: string;
}

export const WELCOME_NOTE_TITLE_MAX = 80;
export const WELCOME_NOTE_BODY_MAX = 1000;

/** Mirrors the backend cap on `memberBillingUrl` (ENG-1762). */
export const MEMBER_BILLING_URL_MAX = 2048;

/**
 * Client-side mirror of the backend's `memberBillingUrl` rule, so ops see the
 * problem before the save 400s: blank is fine (it clears the field),
 * otherwise an absolute https URL, at most 2048 characters, no credentials.
 * Returns the message to show, or null when the value is sendable.
 */
export const memberBillingUrlError = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.length > MEMBER_BILLING_URL_MAX) {
    return `Too long (${trimmed.length}/${MEMBER_BILLING_URL_MAX} characters)`;
  }
  // The backend stores this as an AppSync AWSURL, which refuses more than
  // `new URL` does; these mirror the server's extra rules.
  if (!/^[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+$/.test(trimmed)) {
    return 'Spaces, accented letters and | { } " < > \\ ^ ` must be percent-encoded';
  }
  if (/%(?![0-9A-Fa-f]{2})/.test(trimmed)) return "Malformed %-escape";
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return "Enter a full URL, starting with https://";
  }
  if (url.protocol !== "https:") return "Must start with https://";
  if (!url.hostname) return "Enter a full URL, starting with https://";
  if (!/^[a-z0-9.-]+$/.test(url.hostname)) {
    return "The domain may only contain letters, digits, dots and hyphens";
  }
  if (url.username || url.password) {
    return "Must not contain a username or password";
  }
  if (url.pathname.includes("//")) return "The path must not contain //";
  if (/[[\]]/.test(url.pathname)) return "The path must not contain [ or ]";
  return null;
};

/**
 * Enum values a partner's `capabilities` list may contain (ENG-1641). Today
 * only `manage_members` exists (portal/API add + remove); a missing or empty
 * list means the roster is read-only (checkout-managed).
 */
export type PartnerCapability = "manage_members";

export const PARTNER_CAPABILITIES: {
  value: PartnerCapability;
  label: string;
  help: string;
}[] = [
  {
    value: "manage_members",
    label: "Manage members",
    help: "Portal and API may add and remove members. Empty = read-only roster.",
  },
];

export interface Partner {
  id: string;
  groupId: string;
  displayName: string;
  hashtag: string;
  aboutText?: string;
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  links?: PartnerLink[];
  discourseGroupName?: string;
  discourseCategoryUrl?: string;
  status: PartnerStatus;
  // Per-partner transactional-email toggles (ENG-895). Nullable: absent ⇒ send.
  sendWelcomeEmail?: boolean;
  sendOffboardingEmail?: boolean;
  /**
   * Stable session keys marked as this partner's "preferred" sessions (ENG-990).
   * Each entry is a meeting's stable key (recurringMeetingId ?? gcalEventId ?? id).
   */
  preferredSessionIds?: string[];
  welcomeNote?: PartnerWelcomeNote | null;
  /**
   * What the partner's portal/API may do (ENG-1641). Null ⇒ un-migrated
   * partner, treated as read-only (checkout-managed) the same as [].
   */
  capabilities?: PartnerCapability[] | null;
  /** Kill switch for the whole portal/API surface for this partner. */
  portalEnabled?: boolean | null;
  /**
   * Where members whose access comes from this partner are sent to manage or
   * cancel (ENG-1762). Null ⇒ they're pointed at Focused support. Admin-only.
   */
  memberBillingUrl?: string | null;
  /**
   * Slug of the Mandrill template this partner's welcome email is sent with.
   * Null ⇒ the default `partner-welcome` template. Admin-only.
   */
  welcomeEmailTemplate?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePartnerRequest {
  displayName: string;
  hashtag: string;
  aboutText?: string;
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  links?: PartnerLink[];
  discourseGroupName?: string;
  discourseCategoryUrl?: string;
  sendWelcomeEmail?: boolean;
  sendOffboardingEmail?: boolean;
  /** Absolute https URL, or null/absent to leave it unset. */
  memberBillingUrl?: string | null;
}

export type UpdatePartnerRequest = Partial<CreatePartnerRequest> & {
  status?: PartnerStatus;
  /**
   * Full replacement of the partner's preferred session keys. Absent ⇒ leave
   * unchanged; sending the array REPLACES the stored list. Cannot be combined
   * with the add/remove intents below.
   */
  preferredSessionIds?: string[];
  /**
   * Add/remove intents applied SERVER-SIDE against the stored list — prefer
   * these over the full replacement so a stale client snapshot can't drop
   * keys other admins added meanwhile.
   */
  addPreferredSessionIds?: string[];
  removePreferredSessionIds?: string[];
  /**
   * The first-session greeting. Absent ⇒ leave the stored note unchanged;
   * null ⇒ clear it.
   */
  welcomeNote?: PartnerWelcomeNote | null;
  /** Full replacement of the partner's capability list. [] ⇒ read-only roster. */
  capabilities?: PartnerCapability[] | null;
  /** Kill switch for the whole portal/API surface for this partner. */
  portalEnabled?: boolean | null;
  /**
   * The member billing page. Absent ⇒ leave unchanged; null ⇒ clear it
   * (members are then pointed at Focused support).
   */
  memberBillingUrl?: string | null;
  /**
   * Mandrill template name or slug for the welcome email. Absent ⇒ leave
   * unchanged; null ⇒ clear it (back to the default template). The server
   * checks it with Mandrill and 400s with INVALID_WELCOME_EMAIL_TEMPLATE.
   */
  welcomeEmailTemplate?: string | null;
};

/** `ApiError.code` for a welcome template Mandrill can't send with. */
export const INVALID_WELCOME_EMAIL_TEMPLATE = "invalid_welcome_email_template";

interface PartnersListResponse {
  partners: Partner[];
}

export interface AssetUploadUrlRequest {
  fileName: string;
  contentType: string;
}

export interface AssetUploadUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  s3Key: string;
}

export interface PartnerMember {
  appUserId: string;
  email: string;
  displayName: string;
  revenueCatId: string;
  /** Account creation date (ISO 8601); "" when unknown. */
  createdAt: string;
  /** Partner's staff/coach flag (ENG-1643). Excluded from the billable count. */
  isStaff: boolean;
  /** ISO 8601 timestamp when the promo entitlement was skipped because the
   * member already had their own active subscription; null otherwise. */
  entitlementSkippedAt: string | null;
  /**
   * Whether this member is in the `Partners` Cognito group. Null when the
   * Cognito lookup failed server-side (render as unknown, not "false").
   */
  portalAccess: boolean | null;
}

/** A partner staff member who can host its sessions (ENG-1936). */
export interface PartnerHost {
  /** The Focused Space app user id — the id a meeting's host schedule names. */
  appUserId: string;
  displayName: string;
  email: string;
}

interface PartnerHostsResponse {
  hosts: PartnerHost[];
}

export type ProvisionStatus = "created" | "attached" | "already_member";

export interface ProvisionResult {
  email: string;
  status: ProvisionStatus;
  appUserId: string;
  revenueCatId: string;
  alreadyHadActiveSubscription: boolean;
}

export interface OffboardResult {
  email: string;
  appUserId: string;
  revenueCatId: string;
  rcRevoked: boolean;
  userGroupDeleted: boolean;
  tagDeleted: boolean;
}

export interface BulkRowResult {
  email: string;
  displayName: string;
  status: ProvisionStatus | "error";
  appUserId?: string;
  alreadyHadActiveSubscription?: boolean;
  error?: string;
}

export interface BulkImportSummary {
  total: number;
  created: number;
  attached: number;
  alreadyMember: number;
  error: number;
  alreadyHadActiveSubscription: number;
}

export interface BulkImportResponse {
  summary: BulkImportSummary;
  results: BulkRowResult[];
}

interface PartnerMembersResponse {
  members: PartnerMember[];
}

export interface PortalAccessResult {
  appUserId: string;
  portalAccess: boolean;
  isStaff: boolean;
}

/** Identifier refs of a meeting scoped to a partner (partner-only). */
export interface ScopedMeetingRef {
  externalMeetingId: string;
  gcalEventId: string | null;
  recurringMeetingId: string | null;
}

interface ScopedMeetingsResponse {
  meetings: ScopedMeetingRef[];
}

// Small in-memory cache for the partner list — it's read on every meeting's
// Partner Association section/read-out, changes rarely, and is invalidated on
// any create/update below.
let cachedPartners: { at: number; data: Partner[] } | null = null;
const PARTNERS_CACHE_TTL_MS = 60_000;
const invalidatePartnersCache = () => {
  cachedPartners = null;
};

// Backends that predate ENG-1643, or a PATCH response that only echoes the
// field it wrote, can omit isStaff/entitlementSkippedAt/portalAccess
// entirely. Normalize at the boundary so every caller sees the documented
// shape (portalAccess null, never undefined, for "unknown").
const normalizeMember = (member: PartnerMember): PartnerMember => ({
  ...member,
  isStaff: member.isStaff ?? false,
  entitlementSkippedAt: member.entitlementSkippedAt ?? null,
  portalAccess: member.portalAccess ?? null,
});

const normalizePortalAccessResult = (
  result: PortalAccessResult,
): PortalAccessResult => ({
  ...result,
  isStaff: result.isStaff ?? false,
  portalAccess: result.portalAccess ?? false,
});

// Per-partner cache for member rosters. The details and members dialogs both
// re-read the whole roster on open; it only changes through the add/remove/bulk
// calls below, each of which invalidates its partner's entry.
const cachedMembers = new Map<string, { at: number; data: PartnerMember[] }>();
const MEMBERS_CACHE_TTL_MS = 60_000;
// Per-partner cache for the staff hosts the meeting host pickers offer. Who
// counts as staff changes only through the member calls below, so the roster
// invalidation drops this entry too.
const cachedHosts = new Map<string, { at: number; data: PartnerHost[] }>();
const invalidateMembersCache = (partnerId: string) => {
  cachedMembers.delete(partnerId);
  cachedHosts.delete(partnerId);
};

// Per-partner cache for scoped-meeting refs (calendar Partner filter/badges).
// Short TTL as a safety net; the meeting edit dialog also invalidates it
// explicitly (via updateMeeting) since scope changes happen there, not through
// any PartnerResource call.
const cachedScopedMeetings = new Map<
  string,
  { at: number; promise: Promise<ScopedMeetingRef[]> }
>();
const SCOPED_MEETINGS_CACHE_TTL_MS = 30_000;

/** Drop every partner's scoped-meetings cache — a meeting's partner scope
 * changed and the old owner is unknown here. */
export const invalidateScopedMeetingsCache = () => {
  cachedScopedMeetings.clear();
};

export const PartnerResource = {
  getList: () =>
    apiFetch<PartnersListResponse>("/admin/partners").then((r) => r.partners),

  /** getList with a short in-memory cache (invalidated on create/update). */
  getListCached: async (): Promise<Partner[]> => {
    if (
      cachedPartners &&
      Date.now() - cachedPartners.at < PARTNERS_CACHE_TTL_MS
    ) {
      return cachedPartners.data;
    }
    const data = await PartnerResource.getList();
    cachedPartners = { at: Date.now(), data };
    return data;
  },

  listMembers: async (partnerId: string): Promise<PartnerMember[]> => {
    const cached = cachedMembers.get(partnerId);
    if (cached && Date.now() - cached.at < MEMBERS_CACHE_TTL_MS) {
      return cached.data;
    }
    const { members } = await apiFetch<PartnerMembersResponse>(
      `/admin/partner/${partnerId}/members`,
    );
    const normalized = members.map(normalizeMember);
    cachedMembers.set(partnerId, { at: Date.now(), data: normalized });
    return normalized;
  },

  /** The partner's staff who can host its sessions, sorted by display name. */
  listHosts: async (partnerId: string): Promise<PartnerHost[]> => {
    const cached = cachedHosts.get(partnerId);
    if (cached && Date.now() - cached.at < MEMBERS_CACHE_TTL_MS) {
      return cached.data;
    }
    const { hosts } = await apiFetch<PartnerHostsResponse>(
      `/admin/partner/${partnerId}/hosts`,
    );
    cachedHosts.set(partnerId, { at: Date.now(), data: hosts });
    return hosts;
  },

  addMember: (
    partnerId: string,
    data: { email: string; displayName: string; password?: string },
  ) =>
    apiFetch<ProvisionResult>(`/admin/partner/${partnerId}/member`, {
      method: "POST",
      body: data,
    }).then((r) => {
      invalidateMembersCache(partnerId);
      return r;
    }),

  removeMember: (
    partnerId: string,
    ref: { appUserId?: string; email?: string },
  ) =>
    apiFetch<OffboardResult>(`/admin/partner/${partnerId}/member`, {
      method: "DELETE",
      body: ref,
    }).then((r) => {
      invalidateMembersCache(partnerId);
      return r;
    }),

  setMemberStaff: (partnerId: string, appUserId: string, isStaff: boolean) =>
    apiFetch<PartnerMember>(`/admin/partner/${partnerId}/member/${appUserId}`, {
      method: "PATCH",
      body: { isStaff },
    }).then((r) => {
      invalidateMembersCache(partnerId);
      return normalizeMember(r);
    }),

  setPortalAccess: (partnerId: string, appUserId: string, grant: boolean) =>
    apiFetch<PortalAccessResult>(`/admin/partner/${partnerId}/portal-access`, {
      method: "POST",
      body: { appUserId, grant },
    }).then((r) => {
      invalidateMembersCache(partnerId);
      return normalizePortalAccessResult(r);
    }),

  bulkImport: (partnerId: string, csv: string) =>
    apiFetch<BulkImportResponse>(`/admin/partner/${partnerId}/members/bulk`, {
      method: "POST",
      body: { csv },
    }).then((r) => {
      invalidateMembersCache(partnerId);
      return r;
    }),

  /** Meetings scoped to this partner (partner-only), as identifier refs.
   * Cached + in-flight-deduped per partner. */
  getScopedMeetingsCached: (partnerId: string): Promise<ScopedMeetingRef[]> => {
    const hit = cachedScopedMeetings.get(partnerId);
    if (hit && Date.now() - hit.at < SCOPED_MEETINGS_CACHE_TTL_MS) {
      return hit.promise;
    }
    const promise = apiFetch<ScopedMeetingsResponse>(
      `/admin/partner/${partnerId}/scoped-meetings`,
    ).then((r) => r.meetings);
    cachedScopedMeetings.set(partnerId, { at: Date.now(), promise });
    // Never cache a rejection — drop it so the next read retries.
    promise.catch(() => {
      if (cachedScopedMeetings.get(partnerId)?.promise === promise) {
        cachedScopedMeetings.delete(partnerId);
      }
    });
    return promise;
  },

  getById: (partnerId: string) =>
    apiFetch<Partner>(`/admin/partner/${partnerId}`),

  create: (data: CreatePartnerRequest) =>
    apiFetch<Partner>("/admin/partner", { method: "POST", body: data }).then(
      (r) => {
        invalidatePartnersCache();
        return r;
      },
    ),

  update: (partnerId: string, data: UpdatePartnerRequest) =>
    apiFetch<Partner>(`/admin/partner/${partnerId}`, {
      method: "PATCH",
      body: data,
    }).then((r) => {
      invalidatePartnersCache();
      return r;
    }),

  getAssetUploadUrl: (partnerId: string, data: AssetUploadUrlRequest) =>
    apiFetch<AssetUploadUrlResponse>(
      `/admin/partner/${partnerId}/asset-upload-url`,
      { method: "POST", body: data },
    ),
};

/**
 * Upload a logo/asset for an existing partner: request a presigned URL, PUT the
 * file straight to S3, and return the public URL to persist as logoUrl.
 */
export const uploadPartnerAsset = async (
  partnerId: string,
  file: File,
): Promise<string> => {
  const { uploadUrl, publicUrl } = await PartnerResource.getAssetUploadUrl(
    partnerId,
    { fileName: file.name, contentType: file.type },
  );

  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Asset upload failed: ${response.status}`);
  }

  return publicUrl;
};
